import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';

import { HybridCarousel, HybridCarouselCard } from 'src/components/hybrid-carousel';
import { CarouselContainer } from 'src/components/hybrid-carousel/hybrid-carousel.styles';
import { Product } from 'src/utils/helpers/product';
import { ProductCarousel } from 'src/components/carousel-v2/types/product-carousel';

import { useCategoryRecommendationsCarousel } from './use-category-recommendations-carousel';

type CategoryRecommendationsCarouselProps = {
  products: Product[];
  loading: boolean;
  heading: string;
  subheading?: string;
  icon?: React.ReactNode;
};

export const CategoryRecommendationsCarousel = React.forwardRef(
  ({ products, loading, heading, subheading, icon }: CategoryRecommendationsCarouselProps, ref): JSX.Element | null => {
    const flags = useFlags();
    const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;

    const {
      theme,
      dimensions,
      viewportVisibility,
      handleProductClick,
      handleQuickAddToCart,
    } = useCategoryRecommendationsCarousel();

    if (isProductCardsV2Enabled) {
      return (
        <BorderWrapper>
          <ProductCarousel
            products={products}
            isLoading={loading}
            title={heading}
            subTitle={subheading}
            icon={icon}
            mt='0'
          />
        </BorderWrapper>
      );
    }

    return (
      <StyledCarouselContainer {...theme} data-testid='category-recommendations-carousel' border ref={ref}>
        <HybridCarousel
          icon={icon}
          subheading={subheading}
          heading={heading}
          loading={loading}
          viewportVisibility={viewportVisibility}
          carouselTheme={theme}
          dimensions={dimensions}
        >
          {products.map((product: Product, index) => (
            <HybridCarouselCard
              carouselTheme={theme}
              dimensions={dimensions}
              key={product.id}
              product={product}
              productIndex={index}
              onClick={() => handleProductClick(product, index)}
              onQuickAddClick={() => handleQuickAddToCart(product, index)}
            />
          ))}
        </HybridCarousel>
      </StyledCarouselContainer>
    );
  }
);

const StyledCarouselContainer = styled(CarouselContainer)`
  button[class^='circle-button'] {
    top: 10px;
  }
`;

const BorderWrapper = styled.div`
  border: solid #e3e7e9;
  border-width: 1px 0;
  padding: 24px 0 4px;
`;
